import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { TransactionalQuoteData } from '../../utils/transactionalPricingCalculator';
import { formatTransactionalCurrency, formatTransactionalEmailCount } from '../../utils/transactionalPricingCalculator';
import MailercloudLogo from '../MailercloudLogo';

interface TransactionalQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteData: TransactionalQuoteData;
}

const TransactionalQuoteModal: React.FC<TransactionalQuoteModalProps> = ({ isOpen, onClose, quoteData }) => {
  const printRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const handlePrint = () => {
    // Set document title for better print experience
    const originalTitle = document.title;
    const customerName = quoteData.customerName.replace(/[^a-zA-Z0-9]/g, '_');
    const date = new Date().toISOString().split('T')[0];
    document.title = `Transactional_Quote_${customerName}_${date}`;
    
    window.print();
    
    // Restore original title after print
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownload = () => {
    // Set document title for PDF filename
    const originalTitle = document.title;
    const customerName = quoteData.customerName.replace(/[^a-zA-Z0-9]/g, '_');
    const date = new Date().toISOString().split('T')[0];
    document.title = `Transactional_Quote_${customerName}_${date}`;
    
    window.print();
    
    // Restore original title after print
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const { pricing } = quoteData;

  // Create reusable quote content component
  const QuoteContent = () => (
    <div className="quote-document">
      {/* Company Header */}
      <div className="quote-header">
        <div className="logo-section">
          <MailercloudLogo size="lg" variant="horizontal" />
        </div>
        <div className="company-details">
          <h3>Mailercloud</h3>
          <p>Email Infrastructure Solutions</p>
          <p>sales@mailercloud.com</p>
          <p>www.mailercloud.com</p>
        </div>
      </div>

      {/* Quote Details */}
      <div className="quote-info">
        <div className="quote-number">
          <h2>QUOTATION</h2>
          <p>Quote No: {quoteData.quoteNumber}</p>
          <p>Date: {quoteData.quoteDate}</p>
        </div>
      </div>

      {/* Customer Details */}
      <div className="customer-section">
        <h3>Prepared For:</h3>
        <p className="customer-name">{quoteData.customerName}</p>
        <p className="company-name">{quoteData.companyName}</p>
      </div>

      {/* Service Details */}
      <div className="service-section">
        <h3>Service: Transactional Email (SMTP/API)</h3>
        <table className="quote-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Volume</th>
              <th>Rate</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Transactional Emails</strong>
                <br />
                <span className="item-details">{pricing.slab.label}</span>
              </td>
              <td>{formatTransactionalEmailCount(pricing.totalEmails)} emails/month</td>
              <td>
                {formatTransactionalCurrency(pricing.slab.pricePerThousand, pricing.currency)} per 1,000
              </td>
              <td className="amount">
                {formatTransactionalCurrency(pricing.monthlyEmailPrice, pricing.currency)}
              </td>
            </tr>
            
            {pricing.discountPercentage > 0 && (
              <tr className="discount-row">
                <td>
                  <strong>Discount ({pricing.discountPercentage}%)</strong>
                  <br />
                  {quoteData.discountReason && (
                    <span className="item-details">
                      Reason: {quoteData.discountReason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  )}
                </td>
                <td>-</td>
                <td>Applied to email costs only</td>
                <td className="amount discount-amount">
                  -{formatTransactionalCurrency(pricing.discountAmount, pricing.currency)}
                </td>
              </tr>
            )}
            
            {pricing.dedicatedIpCount > 0 && (
              <tr>
                <td>
                  <strong>Dedicated IP Address{pricing.dedicatedIpCount > 1 ? 'es' : ''}</strong>
                  <br />
                  <span className="item-details">Improved deliverability & sender reputation</span>
                </td>
                <td>{pricing.dedicatedIpCount} IP{pricing.dedicatedIpCount > 1 ? 's' : ''}</td>
                <td>
                  {formatTransactionalCurrency(pricing.pricePerIp, pricing.currency)} each/month
                </td>
                <td className="amount">
                  {formatTransactionalCurrency(pricing.dedicatedIpPrice, pricing.currency)}
                </td>
              </tr>
            )}
            
            <tr className="total-row">
              <td colSpan={3} className="total-label">
                <strong>Total Monthly Cost</strong>
              </td>
              <td className="total-amount">
                {formatTransactionalCurrency(pricing.totalMonthlyPrice, pricing.currency)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Cost per email */}
        <div className="additional-info">
          <p>
            <strong>Cost per email:</strong> {formatTransactionalCurrency(pricing.totalMonthlyPrice / pricing.totalEmails, pricing.currency)}
          </p>
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <h3>Included Features:</h3>
        <div className="features-grid">
          <ul>
            <li>Real-time email delivery via SMTP/API</li>
            <li>99.9% uptime guarantee</li>
            <li>Detailed delivery reports & analytics</li>
            <li>Webhooks for real-time event tracking</li>
          </ul>
          <ul>
            <li>Advanced email authentication (SPF, DKIM, DMARC)</li>
            <li>24/7 technical support</li>
            <li>RESTful API with comprehensive documentation</li>
            <li>SMTP relay with multiple ports support</li>
          </ul>
        </div>
      </div>

      {/* Terms */}
      <div className="terms-section">
        <h3>Terms & Conditions:</h3>
        <ul>
          <li>Pricing is based on monthly email volume</li>
          <li>Unused emails do not roll over to the next month</li>
          <li>Payment terms: Monthly in advance</li>
          <li>This quote is valid for 30 days from the date of issue</li>
          {pricing.dedicatedIpCount > 0 && (
            <li>
              Dedicated IP{pricing.dedicatedIpCount > 1 ? 's require' : ' requires'} a minimum 3-month commitment
            </li>
          )}
        </ul>
      </div>

      {/* Footer */}
      <div className="quote-footer">
        {quoteData.generatedBy && (
          <div className="generated-by">
            <p>Prepared by: {quoteData.generatedBy.name}</p>
            <p>Email: {quoteData.generatedBy.email}</p>
          </div>
        )}
        <div className="footer-note">
          <p>Thank you for considering Mailercloud for your transactional email needs!</p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Modal Version */}
      <div className="quote-modal-overlay" onClick={onClose}>
        <div className="quote-modal" onClick={(e) => e.stopPropagation()}>
          <div className="quote-modal-header">
            <h2>Transactional Email Quote</h2>
            <button className="close-button" onClick={onClose} aria-label="Close modal">
              ×
            </button>
          </div>

          <div className="quote-modal-content" ref={printRef}>
            <QuoteContent />
          </div>

          <div className="quote-modal-actions">
            <button onClick={handlePrint} className="action-button print-button">
              🖨️ Print Quote
            </button>
            <button onClick={handleDownload} className="action-button download-button">
              📥 Download PDF
            </button>
            <button onClick={onClose} className="action-button close-action">
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Print-only version (hidden on screen) - Rendered to body using Portal */}
      {ReactDOM.createPortal(
        <div className="quote-print-version">
          <QuoteContent />
        </div>,
        document.body
      )}
    </>
  );
};

export default TransactionalQuoteModal;